import { gsap } from 'gsap'
import { createElement } from '@/_utilities/dom'
import { palette } from '@/_constants/palette'

/**
 * A single streaking star in the hyperspace intro effect.
 */
interface WarpStar {
  x: number
  y: number
  z: number
  previousZ: number
}

/**
 * The cinematic loading screen: a canvas hyperspace tunnel, the QuickCasa wordmark
 * materializing from the streaks, a tagline, and an "Enter the Universe" button.
 * Resolving the run promise hands control to the 3D scene, and the button click is
 * the user gesture that unlocks audio.
 */
export class LoadingScreen {
  private readonly container: HTMLDivElement
  private readonly canvas: HTMLCanvasElement
  private readonly context: CanvasRenderingContext2D
  private readonly content: HTMLDivElement
  private readonly stars: WarpStar[]
  private readonly tints: string[]
  private animationHandle: number
  private speed: number
  private isRunning: boolean

  /**
   * Build the loading screen DOM and prime the hyperspace field.
   */
  public constructor() {
    this.stars = []
    this.tints = [palette.textPrimary, palette.secondaryGlow, palette.primaryGlow, palette.nebulaMagenta]
    this.animationHandle = 0
    this.speed = 0.6
    this.isRunning = false

    this.container = createElement('div', { className: 'loading-screen' })
    this.canvas = createElement('canvas', { className: 'loading-canvas' })
    const drawingContext = this.canvas.getContext('2d')
    if (drawingContext === null) {
      throw new Error('Unable to acquire a 2D drawing context for the loading screen.')
    }
    this.context = drawingContext

    this.content = createElement('div', { className: 'loading-content' })
    const wordmark = createElement('h1', { className: 'loading-wordmark', text: 'QUICKCASA' })
    const tagline = createElement('p', { className: 'loading-tagline', text: 'Everything we build, in one place' })
    const button = createElement('button', { className: 'loading-enter', text: 'Explore QuickCasa' })
    this.content.append(wordmark, tagline, button)

    this.container.append(this.canvas, this.content)

    this.resize = this.resize.bind(this)
    this.tick = this.tick.bind(this)
  }

  /**
   * Mount the loading screen, run the warp animation, and resolve once the user
   * enters. The provided gesture callback fires synchronously inside the click.
   *
   * @param {HTMLElement} mountPoint - The element to mount the screen into.
   * @param {() => void} onEnterGesture - Runs within the entering click gesture.
   * @returns {Promise<void>} Resolves once the screen has been dismissed.
   */
  public run(mountPoint: HTMLElement, onEnterGesture: () => void): Promise<void> {
    mountPoint.append(this.container)
    this.resize()
    window.addEventListener('resize', this.resize)
    this.seedStars(420)
    this.isRunning = true
    this.animationHandle = window.requestAnimationFrame(this.tick)

    gsap.to(this, { speed: 5.5, duration: 2.4, ease: 'power3.in' })
    gsap.fromTo(
      this.content,
      { opacity: 0, scale: 0.86 },
      { opacity: 1, scale: 1, duration: 1.4, delay: 1.6, ease: 'power2.out' },
    )

    return new Promise((resolve) => {
      const enterButton = this.content.querySelector('.loading-enter')
      if (enterButton === null) {
        resolve()
        return
      }
      enterButton.addEventListener('click', () => {
        onEnterGesture()
        this.dismiss(resolve)
      })
    })
  }

  /**
   * Fade the screen out, stop the animation, and resolve the run promise.
   *
   * @param {() => void} resolve - The run promise resolver.
   * @returns {void}
   */
  private dismiss(resolve: () => void): void {
    gsap.to(this, { speed: 22, duration: 0.7, ease: 'power4.in' })
    gsap.to(this.container, {
      opacity: 0,
      duration: 0.8,
      ease: 'power2.inOut',
      onComplete: () => {
        this.isRunning = false
        window.cancelAnimationFrame(this.animationHandle)
        window.removeEventListener('resize', this.resize)
        this.container.remove()
        resolve()
      },
    })
  }

  /**
   * Fill the star field with randomly placed warp stars.
   *
   * @param {number} count - The number of stars to seed.
   * @returns {void}
   */
  private seedStars(count: number): void {
    const width = this.canvas.width
    const height = this.canvas.height
    for (let index = 0; index < count; index += 1) {
      const z = Math.random() * width
      this.stars.push({
        x: (Math.random() - 0.5) * width,
        y: (Math.random() - 0.5) * height,
        z,
        previousZ: z,
      })
    }
  }

  /**
   * Resize the canvas to fill the viewport.
   *
   * @returns {void}
   */
  private resize(): void {
    this.canvas.width = window.innerWidth
    this.canvas.height = window.innerHeight
  }

  /**
   * Advance and render one frame of the hyperspace tunnel.
   *
   * @returns {void}
   */
  private tick(): void {
    if (!this.isRunning) {
      return
    }
    const width = this.canvas.width
    const height = this.canvas.height
    const centreX = width / 2
    const centreY = height / 2

    this.context.fillStyle = 'rgba(10, 10, 26, 0.4)'
    this.context.fillRect(0, 0, width, height)
    this.context.lineCap = 'round'

    for (let index = 0; index < this.stars.length; index += 1) {
      const star = this.stars[index]
      star.previousZ = star.z
      star.z -= this.speed * 8
      if (star.z <= 1) {
        star.x = (Math.random() - 0.5) * width
        star.y = (Math.random() - 0.5) * height
        star.z = width
        star.previousZ = width
      }

      const scale = width / star.z
      const screenX = centreX + star.x * scale
      const screenY = centreY + star.y * scale
      const previousScale = width / star.previousZ
      const previousX = centreX + star.x * previousScale
      const previousY = centreY + star.y * previousScale

      const brightness = Math.min(1, (1 - star.z / width) * 1.4)
      this.context.strokeStyle = this.tints[index % this.tints.length]
      this.context.globalAlpha = brightness
      this.context.lineWidth = Math.max(1, scale * 1.4)
      this.context.beginPath()
      this.context.moveTo(previousX, previousY)
      this.context.lineTo(screenX, screenY)
      this.context.stroke()
    }
    this.context.globalAlpha = 1

    this.animationHandle = window.requestAnimationFrame(this.tick)
  }
}
